import React, { useEffect } from 'react';
import { Flex, useTokens } from '@mezzoforte/forge';
import useSendAnalytics from '@/hooks/useSendAnalytics';
import { OPLink } from '@/features/EntryPage/FinancingOptions/OPRahoitus/util/op-laskuri';
import { gtmService } from '@/util/gtm-service';

const { fontWeights, lineHeights } = useTokens.huutokaupat;

interface OPRahoitusButtonProps {
  readonly loanAmount: number;
  readonly loanAmountMin: number;
  readonly analyticData: {
    readonly entryId: number;
    readonly category: string;
    readonly currentPrice: number;
  };
}

function OPRahoitusButton({ loanAmount, loanAmountMin, analyticData }: OPRahoitusButtonProps) {
  const sendAnalytics = useSendAnalytics('opWidgetProduct', analyticData);
  const { __opCalcWidget: widget }: Window = window;

  useEffect(() => {
    if (widget?.setAmount) {
      // @ts-expect-error not typed
      widget.setAmount(loanAmount);
      // @ts-expect-error not typed
      widget.setAmountMin(loanAmountMin);
    }
  }, [loanAmount, loanAmountMin, widget]);

  const opFormElement = document.querySelector(`form[action="${OPLink}"]`);

  useEffect(() => {
    if (!opFormElement) {
      return;
    }
    opFormElement.addEventListener('submit', () => {
      sendAnalytics();
      gtmService.event('auction:apply_for_financing', { type: 'OP' });
    });
  }, [opFormElement, sendAnalytics]);

  return (
    <Flex
      position="relative"
      width="auto"
      flex="1 0 auto"
      sx={{
        // Branding Buttons
        '.op-keti__widget.op-keti__type--product button.op-keti__btn.op-keti__sum-button': {
          '&:hover:not(:active):not(:focus), &:hover:not(:active):focus': {
            background: 'backgroundGray',
            border: '1px solid',
            borderColor: 'inputBorder',
          },
        },
        '.op-keti__widget.op-keti__type--product button.op-keti__btn.op-keti__sum-button.selected': {
          color: 'textInvert',
          background: 'success',
          border: '1px solid',
          borderColor: 'success',
          '&:hover:not(:active):not(:focus), &:hover:not(:active):focus': {
            background: 'successDark',
            border: '1px solid',
            borderColor: 'successDark',
          },
        },
        '.op-keti__widget.op-keti__type--product fieldset .op-keti__stepper:focus': {
          border: '1px solid',
          borderColor: 'brand',
          opacity: 1,
        },
        '.op-keti__widget.op-keti__type--product fieldset .op-keti__stepper[data-action="down"]:before, .op-keti__widget.op-keti__type--product fieldset .op-keti__stepper[data-action="up"]:before, .op-keti__widget.op-keti__type--product fieldset .op-keti__stepper[data-action="up"]:after':
          {
            background: 'success',
          },

        '.op-keti__widget': {
          display: 'flex',
          width: '100%',

          // Widget FieldSets
          fieldset: {
            display: 'flex !important',
            flexDirection: 'column',

            '> div.op-keti__label-container > label': {
              '> span.op-keti__label--text': {
                display: 'flex',
                justifyContent: 'center',
              },
              '> span.op-keti__label--helper': {
                display: 'flex',
                justifyContent: 'center',
              },
            },
          },

          // Form result text wrap
          'form > div.op-keti__form-submit-wrap > .op-keti__form-submit-btn': {
            background: 'success',
            borderColor: 'success',
            '&:hover': {
              opacity: 1,
              background: 'successDark',
              borderColor: 'successDark',
            },
          },
          'form > div.op-keti__result-rows > div:first-of-type > span > strong:first-of-type': {
            whiteSpace: 'normal',
          },
          'form > div:nth-of-type(3) > p.op-keti__read-more': {
            textAlign: 'center',
            color: 'brand',
            lineHeight: lineHeights.buttonSmall,
            fontWeight: fontWeights.buttonSmall,
            fontFamily: 'var(--heading-font-family)',
            '&:hover': {
              textDecoration: 'underline',
            },
          },

          // Widget Button
          'button.op-keti__btn-open': {
            height: '100%',
            width: '100%',
            border: 0,
            display: 'flex !important',
            whiteSpace: 'pre-wrap',
            margin: 0,
            padding: 0,
            color: 'success',
            background: 'none',
            boxShadow: 'none',
            lineHeight: lineHeights.button,
            fontWeight: fontWeights.button,
            fontFamily: 'var(--heading-font-family)',

            '&:active': {
              background: 'none',
              outline: 'none',
              textDecoration: 'underline',
            },

            '&:hover': {
              color: 'success',
              textDecoration: 'underline',
            },
          },
          'button.op-keti__btn-open:hover, button.op-keti__btn-open:focus': {
            background: 'white',
          },
        },

        '.op-keti__type--product.op-keti--opened .op-keti__container': {
          width: '100%',
        },
      }}
    >
      <div id="op-keti-init-product" />
    </Flex>
  );
}

export default OPRahoitusButton;
